import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle } from "lucide-react";
import { hardenedSubmit, generateConfirmation } from "@/lib/submit";

const inputCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 transition-colors";
const selectCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 appearance-none transition-colors";
const labelCls = "block text-[11px] font-bold text-amber-400/90 uppercase tracking-wider mb-1.5";

/* Confirmation screen */
function ConfirmationScreen({ confirmationNumber, name, onComplete }) {
  const [countdown, setCountdown] = useState(6);
  useState(() => {
    const timer = setInterval(() => {
      setCountdown((p) => { if (p <= 1) { clearInterval(timer); onComplete(); return 0; } return p - 1; });
    }, 1000);
    return () => clearInterval(timer);
  });
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 bg-[#0a0e1a]" data-testid="partner-confirmation-screen">
      <div className="max-w-md w-full rounded-xl border border-slate-700/50 bg-[#111827]/70 p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Application Submitted!</h2>
        <p className="text-sm text-slate-400 mb-6">Thank you, {name}. Your application has been received and is being processed. A member of our team will be in contact shortly.</p>
        <div className="rounded-lg border border-amber-500/30 bg-[#0f1629] p-4 mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">Application Confirmation Number</p>
          <p className="text-xl font-mono font-bold text-amber-400" data-testid="partner-app-confirmation">{confirmationNumber}</p>
        </div>
        <p className="text-sm text-slate-400 mb-4">Returning to home in <span className="text-amber-400 font-bold">{countdown}</span> seconds...</p>
        <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-1000 ease-linear" style={{ width: `${((6 - countdown) / 6) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

export default function ApplyPartnerPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [applicantName, setApplicantName] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", street_address: "", city: "", state: "",
    zip_code: "", ssn: "", date_of_birth: "", monthly_income: "",
    employment_status: "", residence_type: "", desired_loan_amount: "",
    loan_purpose: "", rep_name: "", rep_email: "", rep_company: "",
    company_website: "",
  });

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleRedirect = useCallback(() => { navigate("/"); }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      setSubmitError("");
      const payload = {
        business_name: form.rep_company || form.full_name || "N/A",
        contact_name: form.full_name,
        email: form.email,
        phone: form.phone,
        amount_requested: form.desired_loan_amount || "Not specified",
        street_address: form.street_address,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        ssn: form.ssn,
        date_of_birth: form.date_of_birth,
        monthly_income: form.monthly_income,
        employment_status: form.employment_status,
        residence_type: form.residence_type,
        loan_purpose: form.loan_purpose,
        rep_name: form.rep_name,
        rep_email: form.rep_email,
        rep_company: form.rep_company,
        company_website: form.company_website,
        _client_confirmation: generateConfirmation(),
      };
      const result = await hardenedSubmit("/apply", payload);
      setConfirmationNumber(result.confirmation_number);
      setApplicantName(form.full_name);
      setShowConfirmation(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      setSubmitError(err.message || "Submission failed. Please try again.");
      toast.error(err.message || "Submission failed. Please try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  if (showConfirmation) {
    return <ConfirmationScreen confirmationNumber={confirmationNumber} name={applicantName} onComplete={handleRedirect} />;
  }

  return (
    <div className="py-10 md:py-14 px-4 bg-partner min-h-screen" data-testid="apply-partner-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <Link to="/apply" className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 mb-4" data-testid="back-to-selection">&larr; Back to Selection</Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2" data-testid="page-title">Partner Credit Application</h1>
          <p className="text-sm text-slate-400">For PTC Client Hub + EasyQuickFund Partners. Complete the form below to submit on behalf of your client.</p>
        </div>

        <div className="mb-8 px-5 py-3 rounded-md border border-amber-500/30 bg-amber-500/5 text-center">
          <p className="text-sm text-amber-300 font-medium">This is a soft pull and will NOT result in a new inquiry or harm your credit score.</p>
        </div>

        <div className="rounded-xl border border-slate-700/50 bg-[#111827]/70 backdrop-blur-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit} data-testid="apply-partner-form">
            <div className="hp-field" aria-hidden="true">
              <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set} />
            </div>

            {/* Personal Information */}
            <h2 className="text-lg font-bold text-amber-400 italic mb-5">Personal Information</h2>
            <div className="space-y-4">
              <div><label className={labelCls}>Full Name *</label><input data-testid="field-full-name" className={inputCls} name="full_name" required value={form.full_name} onChange={set} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Email Address *</label><input data-testid="field-email" className={inputCls} name="email" type="email" required value={form.email} onChange={set} /></div>
                <div><label className={labelCls}>Phone Number *</label><input data-testid="field-phone" className={inputCls} name="phone" type="tel" required value={form.phone} onChange={set} /></div>
              </div>
              <div><label className={labelCls}>Street Address *</label><input data-testid="field-street" className={inputCls} name="street_address" required value={form.street_address} onChange={set} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>City *</label><input data-testid="field-city" className={inputCls} name="city" required value={form.city} onChange={set} /></div>
                <div><label className={labelCls}>State *</label><input data-testid="field-state" className={inputCls} name="state" required value={form.state} onChange={set} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Zip Code *</label><input data-testid="field-zip" className={inputCls} name="zip_code" required value={form.zip_code} onChange={set} /></div>
                <div className="hidden sm:block" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Social Security Number *</label><input data-testid="field-ssn" className={inputCls} name="ssn" required value={form.ssn} onChange={set} /></div>
                <div><label className={labelCls}>Date of Birth *</label><input data-testid="field-dob" className={`${inputCls} date-input`} name="date_of_birth" type="date" required value={form.date_of_birth} onChange={set} /></div>
              </div>
            </div>

            {/* Financial Information */}
            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-5">Financial Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Monthly Income *</label><input data-testid="field-monthly-income" className={inputCls} name="monthly_income" required value={form.monthly_income} onChange={set} /></div>
                <div><label className={labelCls}>Employment Status *</label>
                  <select data-testid="field-employment-status" className={selectCls} name="employment_status" required value={form.employment_status} onChange={set}>
                    <option value="">Select Status</option><option value="employed">Employed</option><option value="self_employed">Self-Employed</option><option value="retired">Retired</option><option value="unemployed">Unemployed</option><option value="student">Student</option><option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Residence Type *</label>
                  <select data-testid="field-residence-type" className={selectCls} name="residence_type" required value={form.residence_type} onChange={set}>
                    <option value="">Select Type</option><option value="own">Own</option><option value="rent">Rent</option><option value="other">Other</option>
                  </select>
                </div>
                <div><label className={labelCls}>Desired Loan Amount *</label><input data-testid="field-loan-amount" className={inputCls} name="desired_loan_amount" required value={form.desired_loan_amount} onChange={set} /></div>
              </div>
            </div>

            {/* NO FEE SUMMARY — Partner applications don't show platform fee */}

            <div className="mt-6">
              <label className={labelCls}>Loan Purpose *</label>
              <input data-testid="field-loan-purpose" className={inputCls} name="loan_purpose" required value={form.loan_purpose} onChange={set} />
            </div>

            {/* Representative Information */}
            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-5">Representative Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Rep Name *</label><input data-testid="field-rep-name" className={inputCls} name="rep_name" required value={form.rep_name} onChange={set} /></div>
                <div><label className={labelCls}>Rep Email *</label><input data-testid="field-rep-email" className={inputCls} name="rep_email" type="email" required value={form.rep_email} onChange={set} /></div>
              </div>
              <div><label className={labelCls}>Rep Company *</label><input data-testid="field-rep-company" className={inputCls} name="rep_company" required value={form.rep_company} onChange={set} /></div>
            </div>

            {/* Error banner */}
            {submitError && (
              <div data-testid="submit-error-banner" className="mt-6 mb-4 px-4 py-3 rounded-lg border border-red-500/40 bg-red-500/10">
                <p className="text-sm text-red-400 font-medium">{submitError}</p>
                <p className="text-xs text-red-400/70 mt-1">Your form data is still here. Please try submitting again.</p>
              </div>
            )}

            {/* Security banner */}
            <div data-testid="security-banner" className="mt-8 mb-4 flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-500/20 bg-[#151d2e]">
              <svg className="w-4 h-4 text-amber-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p className="text-xs text-slate-400 leading-relaxed">Your information is encrypted and securely transmitted. We never share your data with third parties.</p>
            </div>

            <button type="submit" disabled={loading} data-testid="partner-submit-btn"
              className="w-full h-12 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold text-sm uppercase tracking-wider transition-all disabled:opacity-50">
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span> : "SUBMIT PARTNER APPLICATION"}
            </button>

            <p className="text-center text-xs text-slate-500 mt-4">
              By submitting, you agree to our <Link to="/privacy" className="text-amber-400 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-amber-400 hover:underline">Terms of Service</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
